import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

import { singleQueryParamValue } from '../utils'

/**
 * Read the current query parameters in a way that is accurate for the server & client.
 *
 * Certain query parameters that we use on the site are not included in the cache key for our Cloudfront distribution.
 * This causes unexpected behavior when trying to read/update query parameter values using `useRouter`, because
 * The server passes down the location information used by `useRouter` to the client which will only include the query parameters that were read on the server.
 *
 * To circumvent this, we can use the query value returned by `useRouter` on initial render to avoid a SSR hydration mismatch,
 * and update the params on the client after the initial render by sourcing the value from `window.location`.
 *
 * This will avoid the possibility of a hydration mismatch while rendering UI based on query parameters, while still ensuring that the actual query params
 * that exist in the URL will be updated on the client if they differ from what was returned by the server.
 *
 * If you are needing to read a query parameter that is a part of our cache key (i.e. `page`, `color`, etc), then you should still use `useRouter().query`.
 * Only use this hook for data that is non-critical and wouldn't be heavily impacted by a state change for the query params after initial client render.
 */
export default function useHydratedParams() {
  const router = useRouter()
  const [params, setParams] = useState(() => {
    return Object.entries(router.query).reduce<{ [key: string]: string }>((acc, [key, value]) => {
      acc[key] = singleQueryParamValue(value, '')
      return acc
    }, {})
  })

  const parseParamsFromRelativePath = useCallback((url: string) => {
    let out: { [key: string]: string } = {}
    try {
      const u = new URL(url, window.location.protocol + '//' + window.location.host)
      for (const [key, value] of u.searchParams) {
        out[key] = value
      }
    } catch (err) {
      console.error(`Unable to parse relative URL "${url}":`, err)
    }
    return out
  }, [])

  useEffect(() => {
    // update params with client values after initial render
    setParams(parseParamsFromRelativePath(window.location.pathname + window.location.search))

    const handleRouteChangeStart = (url: string) => setParams(parseParamsFromRelativePath(url))

    router.events.on('routeChangeStart', handleRouteChangeStart)
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [router, parseParamsFromRelativePath])

  return params
}
